import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FlaskConical, Play, Plus, Trash2, ChevronLeft, Wrench, BookOpen, Brain, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Panel, Tag, Button, Spinner, Textarea, Input } from "@/components/ui";
import { toast } from "sonner";

const STARTER = [
  { name: "Pricing question", messages: ["Hi, do you offer deep cleaning and what does it cost?"] },
  { name: "New booking (collect info)", messages: ["I need a cleaning", "It's a 2-bedroom apartment at 5 Elm St, Austin, this Friday morning"] },
  { name: "Complaint", messages: ["Nobody showed up yesterday and I already paid!"] },
  { name: "Human handoff", messages: ["I want to talk to a real person please"] },
];

function TraceTags({ trace }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {trace.knowledgeUsed && <span className="tag flex items-center gap-1"><BookOpen size={11} /> knowledge</span>}
      {trace.memoryUsed && <span className="tag flex items-center gap-1"><Brain size={11} /> memory</span>}
      {trace.requestCreated && <span className="tag tag-success flex items-center gap-1"><Wrench size={11} /> request{trace.request?.priority === "high" ? " · high" : ""}</span>}
      {(trace.toolCalls || []).length === 0 && <span className="tag" style={{ color: "var(--text-muted)" }}>direct answer</span>}
    </div>
  );
}

export default function Evals() {
  const { active, loading } = useBusiness();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = React.useState(STARTER);
  const [busy, setBusy] = React.useState(false);
  const [results, setResults] = React.useState(null);
  const [metrics, setMetrics] = React.useState(null);

  if (loading) return null;
  if (!active)
    return (
      <Panel className="p-8 text-center">
        <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>Pick a workspace to run evals.</p>
        <Link to="/app" className="btn btn-accent">All workspaces</Link>
      </Panel>
    );

  const updateName = (i, v) => setScenarios((s) => s.map((sc, idx) => (idx === i ? { ...sc, name: v } : sc)));
  const updateMsgs = (i, v) => setScenarios((s) => s.map((sc, idx) => (idx === i ? { ...sc, messages: v.split("\n").map((x) => x.trim()).filter(Boolean) } : sc)));
  const addScenario = () => setScenarios((s) => [...s, { name: `Scenario ${s.length + 1}`, messages: [""] }]);
  const removeScenario = (i) => setScenarios((s) => s.filter((_, idx) => idx !== i));

  const run = async () => {
    const valid = scenarios.filter((s) => s.name && s.messages.length);
    if (!valid.length) { toast.error("Add at least one scenario with a message."); return; }
    setBusy(true); setResults(null); setMetrics(null);
    try {
      const { data } = await api.post(`/businesses/${active.id}/evals/run`, { scenarios: valid, dryRun: true });
      setResults(data.results); setMetrics(data.metrics);
      toast.success(`Ran ${data.scenarioCount} scenario(s)`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <button onClick={() => navigate("/app/dashboard")} className="flex items-center gap-1.5 mb-4 text-sm" style={{ color: "var(--text-muted)" }} data-testid="evals-back">
        <ChevronLeft size={15} /> Dashboard
      </button>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="heading-eyebrow mb-1 flex items-center gap-2"><FlaskConical size={13} style={{ color: "var(--accent-2)" }} /> {active.name}</div>
          <h1 className="text-xl font-semibold">Agent evals</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Simulate customer conversations against the live skills. No WhatsApp is sent and no real requests are created.</p>
        </div>
        <Button variant="accent2" onClick={run} disabled={busy} data-testid="evals-run-btn">
          {busy ? <Spinner size={14} /> : <Play size={15} />} Run evals
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* scenarios */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="heading-eyebrow">Scenarios</span>
            <Button variant="ghost" size="sm" onClick={addScenario} data-testid="evals-add"><Plus size={14} /> Add</Button>
          </div>
          {scenarios.map((sc, i) => (
            <Panel key={i} className="p-4" data-testid={`scenario-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input value={sc.name} onChange={(e) => updateName(i, e.target.value)} className="flex-1" data-testid={`scenario-name-${i}`} />
                <button onClick={() => removeScenario(i)} className="btn btn-ghost btn-sm" data-testid={`scenario-remove-${i}`}><Trash2 size={14} /></button>
              </div>
              <Textarea rows={3} defaultValue={sc.messages.join("\n")} onChange={(e) => updateMsgs(i, e.target.value)}
                placeholder="One customer message per line (sent in order)" data-testid={`scenario-msgs-${i}`} />
              <p className="mono text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>{sc.messages.length} message(s) · one per line</p>
            </Panel>
          ))}
        </div>

        {/* results */}
        <div className="space-y-4">
          <span className="heading-eyebrow">Results</span>
          {metrics && (
            <div className="grid grid-cols-4 gap-px" style={{ background: "var(--border)" }} data-testid="evals-metrics">
              {[["Turns", metrics.totalTurns], ["Requests", metrics.requestsCreated], ["Knowledge", metrics.knowledgeUsed], ["Memory", metrics.memoryUsed]].map(([l, v]) => (
                <div key={l} className="p-3" style={{ background: "var(--s1)" }}>
                  <div className="text-xl font-semibold mono">{v}</div>
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{l}</div>
                </div>
              ))}
            </div>
          )}
          {!results && !busy && (
            <Panel className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="evals-empty">
              Run the scenarios to see how the agent responds turn by turn.
            </Panel>
          )}
          {busy && <Panel className="p-8 flex items-center justify-center"><Spinner size={20} /></Panel>}
          {results && results.map((r, ri) => (
            <Panel key={ri} className="p-4" data-testid={`result-${ri}`}>
              <div className="text-sm font-medium mb-2">{r.name}</div>
              <div className="space-y-3">
                {r.turns.map((t, ti) => (
                  <motion.div key={ti} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ti * 0.03 }}>
                    <div className="text-sm px-3 py-2" style={{ background: "var(--s1)" }}>
                      <span className="mono text-[10px] mr-2" style={{ color: "var(--accent)" }}>IN</span>{t.customer}
                    </div>
                    <div className="text-sm px-3 py-2 mt-1" style={{ background: "var(--s2)", color: "var(--text-2)", whiteSpace: "pre-wrap" }}>
                      <span className="mono text-[10px] mr-2" style={{ color: "var(--accent-2)" }}>OUT</span>{t.agent}
                      <TraceTags trace={t.trace} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </Panel>
          ))}
        </div>
      </div>
    </div>
  );
}
